<script setup lang="ts">
// NPM
import { computed, nextTick, onBeforeMount, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { createI18nMessage, required } from '@vuelidate/validators';
import { zodEmailValidator } from '@/utils/emailValidation';

// Components
import {
  BaseAvatar,
  BaseButton,
  BaseIcon,
  BaseLoader,
  FieldInput,
} from '@magma-app/magma-lapilli';

// Stores
import { useAuthStore } from '@/stores/auth';
import { isSafeReturnPath, peekReturnTo, saveReturnTo } from '@/services/return-to';
const authStore = useAuthStore();

// Plugins
const route = useRoute();
const { locale, t } = useI18n();
const withI18nMessage = createI18nMessage({ t });

// Reactive variables
const disableBtn = ref(false);
const loginAs = ref(false);
const failure = ref(false);
const genericLogin = ref(true);
const success = ref(false);
const selectedOrganizationUuid = ref<string | null>(null);
const selectionLoading = ref(false);

const formData = reactive({
  email: '',
});

type OrganizationLoginOption = {
  uuid: string;
  name: string;
  personas: Array<'admin' | 'ambassador' | 'prospect'>;
  logoUrl?: string | null;
};

const organizations = computed<OrganizationLoginOption[]>(
  () => (authStore.loginAs?.organizations || []) as OrganizationLoginOption[]
);
const selectedOrganization = computed(() => {
  if (!selectedOrganizationUuid.value) return null;
  return (
    organizations.value.find(
      (organization) => organization.uuid === selectedOrganizationUuid.value
    ) || null
  );
});

const personaLabel = (persona: 'admin' | 'ambassador' | 'prospect') => {
  if (persona === 'admin') return t('generic.admin');
  if (persona === 'ambassador') return t('generic.ambassador');
  return t('generic.prospect');
};

const formatPersonas = (personas: Array<'admin' | 'ambassador' | 'prospect'>) =>
  personas.map(personaLabel).join(' / ');

// Form validation
const v$ = useVuelidate(
  {
    email: {
      required: withI18nMessage(required, {
        messagePath: () => t('loginView.emailRequired'),
      }),
      email: withI18nMessage(zodEmailValidator, {
        messagePath: () => t('validation.invalidEmail'),
      }),
    },
  },
  formData
);

// Watchers
watch(
  () => formData.email,
  async () => {
    if (disableBtn.value) {
      disableBtn.value = false;
    }
  }
);

watch(
  () => authStore.loginAs?.organizations,
  (orgs) => {
    if (orgs?.length === 1) {
      selectedOrganizationUuid.value = orgs[0].uuid;
    } else {
      selectedOrganizationUuid.value = null;
    }
  }
);

// The magic link should bring the visitor back to whatever they were opening.
// `peek` (not consume): the form may be abandoned or resubmitted.
const returnTo = () => peekReturnTo() ?? undefined;

// Lifecycle hooks
onBeforeMount(async () => {
  // A `?redirect=` on /login (e.g. from a shared action link) is a target too.
  const redirectQuery = route?.query.redirect;
  if (isSafeReturnPath(redirectQuery)) saveReturnTo(redirectQuery);

  // Get sign in data
  const host = window.location.host;
  const subdomain = host.split('.')[0];

  if ((subdomain && subdomain === 'login') || subdomain === 'staging-login') {
    genericLogin.value = true;
  } else if ((subdomain && subdomain != 'v3') || route?.params.campaignUuid) {
    await authStore.getSignInData(
      subdomain,
      route?.params.campaignUuid as string
    );

    if (authStore.signInData) {
      locale.value = authStore.signInData.language;
    }
  }
});

// Methods
const onChooseProduct = async (
  product: 'ambassador' | 'admin' | 'prospect',
  organizationUuid?: string
) => {
  if (selectionLoading.value) return;
  const organizationToUse = organizationUuid || selectedOrganizationUuid.value;
  if (!organizationToUse) return;

  selectionLoading.value = true;
  try {
    await authStore.signInAs(
      formData.email,
      product,
      organizationToUse,
      returnTo()
    );
    success.value = true;
    loginAs.value = false;
  } catch (error) {
    failure.value = true;
  } finally {
    selectionLoading.value = false;
  }
};

const onChooseOrganization = async (organization: OrganizationLoginOption) => {
  if (selectionLoading.value) return;
  if (organization.personas.length === 1) {
    await onChooseProduct(organization.personas[0], organization.uuid);
    return;
  }

  selectedOrganizationUuid.value = organization.uuid;
};

const submitForm = async () => {
  v$.value.$touch();

  if (await v$.value.$validate()) {
    v$.value.email.$reset();

    if (genericLogin.value) {
      try {
        disableBtn.value = true;
        await authStore.signInAs(
          formData.email,
          undefined,
          undefined,
          returnTo()
        );

        await nextTick();
        disableBtn.value = false;

        if (authStore.loginAs?.needLoginAs) {
          loginAs.value = true;
        } else {
          success.value = true;
        }
      } catch (error) {
        failure.value = true;
        disableBtn.value = true;
      }
    } else {
      try {
        disableBtn.value = true;
        await authStore.signIn(formData.email, returnTo());

        disableBtn.value = false;
        success.value = true;
      } catch (error) {
        failure.value = true;
        disableBtn.value = true;
      }
    }
  }
};
</script>

<template>
  <div class="gradient__grey__07 flex flex-col items-center justify-center p-8">
    <div v-if="!loginAs" class="sm:w-[360px]">
      <img
        class="mx-auto h-20 w-20 rounded-full object-cover ring-[5px] ring-border-on-emphasis"
        :src="
          authStore.signInData?.logoUrl
            ? authStore.signInData?.logoUrl
            : '/img/logo_magma.png'
        "
      />

      <h1
        class="mx-auto mt-8 text-center text-mgm-dspl-xs font-medium text-foreground-emphasis"
      >
        {{
          success
            ? $t('loginView.checkEmail')
            : authStore.signInData?.organizationName
              ? $t('loginView.toDashboard')
              : $t('loginView.login')
        }}
      </h1>

      <p
        class="mx-auto mt-2 text-center text-mgm-txt-sm font-normal text-foreground-default"
      >
        <span
          v-html="
            success
              ? $t('loginView.checkEmailDescription')
              : $t('loginView.description')
          "
        >
        </span>
        <span v-if="success" class="font-medium text-foreground-emphasis">
          {{ formData.email }}
        </span>
      </p>

      <div
        v-if="success"
        class="mx-auto cursor-pointer pt-12 text-center text-mgm-txt-sm font-medium text-foreground-subtle hover:text-border-default"
        @click="
          success = false;
          formData.email = '';
        "
      >
        {{ $t('generic.backToLogin') }}
      </div>

      <div
        v-if="failure"
        class="mt-8 flex w-full items-start rounded-[5px] bg-background-danger-default px-3 py-2"
      >
        <BaseIcon class="mr-2" icon="slash" color="#D92D20" />

        <p class="text-mgm-txt-sm">
          <span class="block font-medium text-foreground-danger-default">{{
            $t('loginView.nonExistentEmail')
          }}</span>

          <span class="block font-normal text-foreground-default">
            {{ $t('loginView.nonExistentEmailDescription') }}
          </span>
        </p>
      </div>

      <template v-if="!success">
        <form class="mt-8" @submit.prevent="submitForm">
          <div>
            <FieldInput
              id="email"
              v-model="formData.email"
              role="email"
              type="email"
              :locale="$i18n.locale"
              :placeholder="$t('loginView.emailPlaceholder')"
              autocomplete="email"
              :validation="v$.email"
              @update:model-value="failure = false"
            />
          </div>

          <BaseButton
            class="mt-6 w-full"
            :disabled="disableBtn"
            :text="$t('loginView.cta')"
            size="md"
            type="submit"
          />
        </form>

        <div class="mt-8 h-px w-full bg-border-subtle"></div>

        <div
          class="mt-8"
          :class="{ flex: authStore.signInData?.contactProfilePicture }"
        >
          <BaseAvatar
            v-if="authStore.signInData?.contactProfilePicture"
            class="mr-4"
            :fallback="
              (authStore.signInData?.contactFirstname || '').charAt(0) +
              (authStore.signInData?.contactLastname || '').charAt(0)
            "
            :src="authStore.signInData?.contactProfilePicture"
            size="md"
          />

          <p
            class="text-mgm-txt-sm font-normal text-foreground-subtle"
            :class="{
              'text-center': !authStore.signInData?.contactProfilePicture
            }"
          >
            <span
              :class="[
                authStore.signInData?.contactProfilePicture ? 'block' : 'mr-1'
              ]"
            >
              {{ $t('loginView.cant') }}
            </span>
            <a
              :href="
                authStore.signInData?.contactEmail
                  ? `mailto:${authStore.signInData?.contactEmail}`
                  : 'mailto:csm@magma.app'
              "
              class="font-medium text-foreground-brand-default hover:text-foreground-brand-hover"
              target="_blank"
            >
              {{
                authStore.signInData?.contactEmail
                  ? $t('loginView.contact')
                  : $t('generic.adminContact')
              }}
            </a>
          </p>
        </div>
      </template>
    </div>

    <div v-else class="flex flex-col items-center justify-center p-8 gap-10">
      <template v-if="selectionLoading">
        <BaseLoader class="min-h-[120px]" />
      </template>

      <template v-else-if="organizations.length > 1 && !selectedOrganization">
        <h1 class="text-mgm-dspl-xs font-medium text-foreground-emphasis">
          {{ $t('loginView.chooseOrganization') }}
        </h1>
        <div class="flex flex-col gap-6">
          <div
            v-for="organization in organizations"
            :key="organization.uuid"
            class="box-border p-3 max-w-sm cursor-pointer rounded-[15px] border border-border-subtle bg-background-default hover:shadow-[0px_12px_16px_-4px_rgba(28,31,39,0.08),0px_4px_6px_-2px_rgba(28,31,39,0.03)] sm:w-[260px] md:w-[330px] lg:w-[384px]"
            @click="onChooseOrganization(organization)"
          >
            <div class="flex flex-row gap-3 items-center">
              <img
                :src="organization.logoUrl || '/img/logo_magma.png'"
                alt="Organization Logo"
                class="w-[56px] rounded-[5px]"
              />
              <div class="flex-1">
                <h2
                  class="text-mgm-txt-md font-medium text-foreground-emphasis"
                >
                  {{ organization.name }}
                </h2>
                <p class="text-mgm-txt-sm font-normal text-foreground-default">
                  {{ formatPersonas(organization.personas) }}
                </p>
              </div>
              <BaseIcon icon="chevron-right" />
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="selectedOrganization">
        <h1
          class="mx-auto text-center text-mgm-dspl-xs font-medium text-foreground-emphasis"
        >
          {{ $t('loginView.logAs') }}
        </h1>

        <div
          class="flex flex-col sm:flex-row gap-8"
          :class="{ 'pointer-events-none opacity-60': selectionLoading }"
        >
          <div
            v-if="selectedOrganization.personas.includes('admin')"
            class="box-border max-w-sm cursor-pointer rounded-[15px] border border-border-subtle bg-background-default hover:shadow-[0px_12px_16px_-4px_rgba(28,31,39,0.08),0px_4px_6px_-2px_rgba(28,31,39,0.03)] sm:w-[260px] md:w-[330px] lg:w-[384px]"
            @click="onChooseProduct('admin')"
          >
            <div
              class="h-[212px] bg-[url('/img/illustration_login_admin.png')] bg-cover bg-center"
            ></div>
            <div class="px-8 pb-8 pt-4">
              <h2 class="text-mgm-txt-md font-medium text-foreground-emphasis">
                {{ $t('generic.admin') }}
              </h2>

              <p class="text-mgm-txt-sm font-normal text-foreground-default">
                {{ $t('loginView.logAdmin') }}
              </p>
            </div>
          </div>

          <div
            v-if="selectedOrganization.personas.includes('ambassador')"
            class="box-border max-w-sm cursor-pointer rounded-[15px] border border-border-subtle bg-background-default hover:shadow-[0px_12px_16px_-4px_rgba(28,31,39,0.08),0px_4px_6px_-2px_rgba(28,31,39,0.03)] sm:w-[260px] md:w-[330px] lg:w-[384px]"
            @click="onChooseProduct('ambassador')"
          >
            <div
              class="h-[212px] bg-[url('/img/illustration_login_helper.png')] bg-cover bg-center"
            ></div>
            <div class="px-8 pb-8 pt-4">
              <h2 class="text-mgm-txt-md font-medium text-foreground-emphasis">
                {{ $t('generic.ambassador') }}
              </h2>

              <p class="text-mgm-txt-sm font-normal text-foreground-default">
                {{ $t('loginView.logHelper') }}
              </p>
            </div>
          </div>

          <div
            v-if="selectedOrganization.personas.includes('prospect')"
            class="box-border max-w-sm cursor-pointer rounded-[15px] border border-border-subtle bg-background-default hover:shadow-[0px_12px_16px_-4px_rgba(28,31,39,0.08),0px_4px_6px_-2px_rgba(28,31,39,0.03)] sm:w-[260px] md:w-[330px] lg:w-[384px]"
            @click="onChooseProduct('prospect')"
          >
            <div
              class="h-[212px] bg-[url('/img/illustration_login_helper.png')] bg-cover bg-center"
            ></div>
            <div class="px-8 pb-8 pt-4">
              <h2 class="text-mgm-txt-md font-medium text-foreground-emphasis">
                {{ $t('generic.prospect') }}
              </h2>

              <p class="text-mgm-txt-sm font-normal text-foreground-default">
                {{ $t('loginView.logHelper') }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
